import React, { useEffect, useState, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
} from "@mui/material";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { AddToFabricationColumns } from "./columns";
import { minimalMuiTableProps } from "../../../../../styles/muiProps";
import { createBulkFabrication } from "../../CommonFeatures/PCSTab/Fabrication/api";
import { useParams } from "react-router-dom";

const AddToFabricationDialog = ({ open, onClose, data, plant = false, plantId }) => {
  const [tableData, setTableData] = useState([]);
  const { id } = useParams();

  useEffect(() => {
    if (open && data) {
      // Initialize table data with DTO fields only
      const initialData = data.map((row) => ({
        ...row,
        "Cost Code": row["Cost Code"] || "Structure",
        "Total Manhour": Number(row["Total Manhour"] || row.estimatedHours || 0),
        "Qty": Number(row.Qty || row.quantity || row["Qty"] || 1),
        "Material Code": row["Material Code"] || row.materialCode || row["Components"] || "",
        "Scope": row.scope || row.Scope || "",
      }));
      setTableData(initialData);
    }
  }, [open, data]);

  // Create editable columns based on DTO structure
  const editableColumns = useMemo(() => {
    return AddToFabricationColumns.map((col) => {
      if (col.accessorKey === "Cost Code") {
        return {
          ...col,
          enableEditing: true,
          muiEditTextFieldProps: () => ({
            children: ["Structure", "Piping", "Electrical", "Other"].map((code) => (
              <MenuItem key={code} value={code}>
                {code}
              </MenuItem>
            )),
            select: true,
          }),
        };
      }
      if (col.accessorKey === "Total Manhour") {
        return {
          ...col,
          header: "Estimated Hours",
          enableEditing: true,
          muiEditTextFieldProps: () => ({
            type: "number",
            inputProps: { min: 0, step: 0.01 },
          }),
        };
      }
      if (col.accessorKey === "Components") {
        return {
          ...col,
          header: "Material Description",
          enableEditing: false, // Material description is read-only
        };
      }
      if (col.accessorKey === "Qty") {
        return {
          ...col,
          header: "Quantity",
          enableEditing: true,
          muiEditTextFieldProps: () => ({
            type: "number",
            inputProps: { min: 0, step: 0.01 },
          }),
        };
      }
      return col;
    }).concat([
      {
        accessorKey: "Material Code",
        header: "Material Code",
        size: 150,
        enableEditing: true,
        enableHiding: false,
        enableSorting: false,
        muiEditTextFieldProps: () => ({
          type: "text",
          placeholder: "Enter material code",
        }),
      },
      {
        accessorKey: "Scope",
        header: "Scope",
        size: 150,
        enableEditing: true,
        enableHiding: false,
        enableSorting: false,
        muiEditTextFieldProps: () => ({
          type: "text",
          placeholder: "Enter scope (optional)",
        }),
      },
    ]);
  }, []);

  const table = useMaterialReactTable({
    columns: editableColumns,
    data: tableData,
    enableRowSelection: true,
    enableEditing: true,
    editDisplayMode: "table", // Enable inline table editing
    getRowId: (row, index) => `row-${index}`, // Provide unique row IDs
    onEditingRowSave: ({ values, row, table }) => {
      const updatedData = tableData.map((item, idx) => {
        // Find the row by comparing with original row data
        if (item === row.original || idx === row.index) {
          return { ...item, ...values };
        }
        return item;
      });
      setTableData(updatedData);
      table.setEditingRow(null);
    },
    muiEditTextFieldProps: ({ cell }) => ({
      onBlur: (event) => {
        // Handle cell-level updates if needed
      },
    }),
    layoutMode: 'grid',
    ...minimalMuiTableProps,
  });

  const handleConfirm = async () => {
    const selectedRows = table.getSelectedRowModel().rows;
    if (selectedRows.length === 0) {
      alert("Please select at least one item to add to fabrication.");
      return;
    }

    const fabricationItems = selectedRows.map((row) => {
      const original = row.original;
      // Map to CreateFabDto structure - only DTO fields
      return {
        costCode: original["Cost Code"] || "Structure",
        estimatedHours: Number(original["Total Manhour"] || original.estimatedHours || 0),
        materialCode: original["Material Code"] || original["Components"] || original.MaterialDescription || original.materialCode || "",
        materialDescription: original["Components"] || original.MaterialDescription || "",
        quantity: Number(original.Qty || original.quantity || original["Qty"] || 1),
        scope: original["Scope"] || original.scope || original.Scope || "",
      };
    });

    console.log("Creating fabrication items:", fabricationItems);
    const parentId = plant ? plantId : id;
    if (fabricationItems.length > 0) {
      try {
        await createBulkFabrication(parentId, fabricationItems, plant);
        alert("Fabrication items added successfully!");
        onClose();
      } catch (error) {
        console.error("Error creating fabrication items:", error);
        alert("Failed to add fabrication items. Please try again.");
      }
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      fullWidth
      maxWidth="xl"
      PaperProps={{
        sx: {
          width: '95vw',
          maxWidth: '1400px',
          height: '90vh',
          maxHeight: '900px',
        }
      }}
    >
      <DialogTitle>Add to Fabrication</DialogTitle>
      <DialogContent
        sx={{
          padding: 2,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          <MaterialReactTable table={table} />
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleConfirm}>
          Add to Fabrication
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddToFabricationDialog;
